import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Image, Eye, Calendar, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import type { Gallery } from '../../lib/supabase';
import { ImageUpload } from './ImageUpload';

interface GalleryFormData {
  title: string;
  description: string;
  image_url: string;
  sort_order: number;
}

const initialFormData: GalleryFormData = {
  title: '',
  description: '',
  image_url: '',
  sort_order: 0
};

export const GalleryManagement: React.FC = () => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<GalleryFormData>(initialFormData);


  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGalleries(data || []);
    } catch (err) {
      console.error('Error fetching gallery:', err);
      setError('Gagal memuat data galeri');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof GalleryFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'sort_order' ? Number(value) : value
    }));
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Get the first profile ID from existing profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);
      
      const profileId = profiles?.[0]?.id || null;
      
      const galleryData = {
        title: formData.title,
        description: formData.description,
        image_url: formData.image_url,
        sort_order: formData.sort_order,
        profile_id: profileId
      };

      if (editingId) {
        // Update existing gallery
        const { error } = await supabase
          .from('gallery')
          .update(galleryData)
          .eq('id', editingId);

        if (error) throw error;
        setSuccess('Data galeri berhasil diperbarui!');
      } else {
        // Create new gallery
        const { error } = await supabase
          .from('gallery')
          .insert([galleryData]);

        if (error) throw error;
        setSuccess('Data galeri berhasil ditambahkan!');
      }

      setFormData(initialFormData);
      setShowForm(false);
      setEditingId(null);
      fetchGalleries();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving gallery:', err);
      setError('Gagal menyimpan data galeri');
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const handleEdit = (gallery: Gallery) => {
    setFormData({
      title: gallery.title,
      description: gallery.description || '',
      image_url: gallery.image_url,
      sort_order: gallery.sort_order || 0
    });
    setEditingId(gallery.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Apakah Anda yakin ingin menghapus foto ini?')) {
      return;
    }

    try {
      setError(null);
      const { error } = await supabase
        .from('gallery')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setSuccess('Foto berhasil dihapus!');
      fetchGalleries();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting gallery:', err);
      setError('Gagal menghapus foto');
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setShowForm(false);
    setEditingId(null);
  };

  // Drag and drop functionality
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = galleries.findIndex((item) => item.id === active.id);
      const newIndex = galleries.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newGalleries = arrayMove(galleries, oldIndex, newIndex);
        
        // Update sort_order for all items
        const updatedGalleries = newGalleries.map((gallery, index) => ({
          ...gallery,
          sort_order: index + 1
        }));
        
        setGalleries(updatedGalleries);
        
        // Update database
        try {
          for (const gallery of updatedGalleries) {
            await supabase
              .from('gallery')
              .update({ sort_order: gallery.sort_order })
              .eq('id', gallery.id);
          }
        } catch (error) {
          console.error('Error updating gallery order:', error);
          // Revert on error
          fetchGalleries();
        }
      }
    }
  };

  // Sortable Gallery Item Component
  const SortableGalleryItem = ({ gallery }: { gallery: any }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: gallery.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition: isDragging ? 'none' : transition,
      opacity: isDragging ? 0.8 : 1,
      zIndex: isDragging ? 1000 : 'auto',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all duration-200 ${
          isDragging ? 'shadow-2xl scale-105 rotate-2 border-blue-300 bg-blue-50' : 'hover:border-gray-300'
        }`}
      >
        <div className="aspect-w-16 aspect-h-9">
          <img
            src={gallery.image_url}
            alt={gallery.title}
            className="w-full h-48 object-cover"
          />
        </div>
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="text-sm font-medium text-gray-900 truncate">
              {gallery.title}
            </h4>
            <div className="flex space-x-1 ml-2">
              <button
                {...attributes}
                {...listeners}
                className="text-gray-400 hover:text-blue-600 cursor-grab active:cursor-grabbing p-1 hover:bg-blue-50 rounded transition-colors duration-150"
                title="Drag untuk mengubah urutan"
              >
                <GripVertical className="h-4 w-4" />
              </button>
              <button
                onClick={() => window.open(gallery.image_url, '_blank')}
                className="text-gray-400 hover:text-gray-600"
                title="Lihat gambar"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleEdit(gallery)}
                className="text-blue-600 hover:text-blue-900"
                title="Edit"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(gallery.id)}
                className="text-red-600 hover:text-red-900"
                title="Hapus"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {gallery.description && (
            <p className="text-xs text-gray-500 mb-2 line-clamp-2">
              {gallery.description}
            </p>
          )}
          
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {new Date(gallery.created_at).toLocaleDateString('id-ID')}
            </div>
            <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
              Urutan: {gallery.sort_order}
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      
    );
  }

  return (
    
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kelola Galeri</h1>
              <p className="mt-1 text-sm text-gray-500">
                Tambah, edit, atau hapus foto yang akan ditampilkan di galeri portofolio
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Tambah Foto
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="text-sm text-green-700">{success}</div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="mb-6 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {editingId ? 'Edit Foto' : 'Tambah Foto Baru'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gambar {!editingId && '*'}
                </label>
                <ImageUpload
                    value={formData.image_url}
                    onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                    bucket="portfolio-images"
                    folder="gallery"
                    placeholder="Upload gambar galeri"
                  />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Judul Foto *
                  </label>
                  <input
                    type="text"
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Judul atau nama foto"
                  />
                </div>

                <div>
                  <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700 mb-2">
                    Urutan Tampil
                  </label>
                  <input
                    type="number"
                    id="sort_order"
                    value={formData.sort_order}
                    onChange={(e) => handleInputChange('sort_order', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>



              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Deskripsi
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ceritakan tentang foto ini..."
                />
              </div>



              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <X className="h-4 w-4 mr-2" />
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.title || !formData.image_url}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingId ? 'Perbarui' : 'Simpan'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Gallery Grid */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Galeri Foto ({galleries.length})
            </h3>
          </div>

          {galleries.length === 0 ? (
            <div className="p-6 text-center">
              <Image className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Belum ada foto</h3>
              <p className="mt-1 text-sm text-gray-500">
                Mulai dengan menambahkan foto pertama ke galeri.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Tambah Foto
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                <strong>💡 Tips:</strong> Gunakan ikon grip (⋮⋮) untuk drag & drop mengubah urutan foto. Foto akan diurutkan sesuai posisi yang Anda atur.
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={galleries.map(g => g.id)} strategy={verticalListSortingStrategy}>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {galleries.map((gallery) => (
                      <SortableGalleryItem key={gallery.id} gallery={gallery} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>
      </div>
    
  );
};